import {
  dispose,
  DomPosition,
  init,
  LineType,
  PolygonType,
  registerIndicator,
  registerOverlay,
  TooltipShowRule,
  type CandleTooltipCustomCallback,
  type Chart as KLineChartInstance,
  type IndicatorDrawParams,
  type IndicatorPolygonStyle,
  type IndicatorStyle,
  type OverlayEvent,
  type SmoothLineStyle,
  type TextStyle,
} from "klinecharts";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Candle } from "../types";
import {
  AXIS_RESERVE,
  fitBarSpace,
  forwardStepMs,
  pctFromStart,
  pricePrecision,
  RIGHT_OFFSET,
  toKLineData,
  withForward,
} from "./chartData";
import { chartTouchAction, wheelIntent } from "./chartGestures";
import { hasMeaningfulVolume } from "../lib/volume";
import {
  DRAWING_TOOL_KLINE_NAMES,
  INDICATOR_KLINE_NAMES,
  type DrawingToolId,
  type IndicatorId,
} from "./chartVocabulary";

/**
 * The chart surface (spec §11 `Chart.tsx`). Wraps KLineChart behind a narrow
 * prop interface — candles, a reveal flag, and opaque overlay values — and
 * knows nothing about scoring, instruments, or what the overlays mean. All the
 * decision logic lives in the pure helpers in chartData.ts so it stays testable
 * without a real canvas; this component only performs the imperative wiring.
 *
 * Anonymization (spec §0 "anonymization includes price"): while not revealed,
 * the y-axis price labels are hidden and only the "% from start" readout is
 * shown. Reveal appends the Forward candles and un-blanks the true prices.
 */

/**
 * Below this container width the OHLC tooltip is wider than the pane and wraps
 * across the candles, so it is suppressed — the same phone degradation the TA
 * toolbar takes (hidden below Tailwind's `sm`).
 */
const COMPACT_CHART_WIDTH = 640;

/**
 * The chart caption on a phone-width pane: the instrument on one line and a
 * trailing parenthesized date range, when there is one, on a muted line below.
 */
function CompactCaption({ caption }: { caption: string }) {
  const match = /^(.*) \((.*)\)$/.exec(caption);
  if (!match) return <>{caption}</>;
  return (
    <>
      {match[1]}
      <span className="block text-[10px] text-muted">{match[2]}</span>
    </>
  );
}

/**
 * How many Forward candles a phone-width pane keeps room for beyond the ones
 * it is showing. A desktop pane reserves the whole Forward up front so the
 * reveal appends without re-fitting; on a phone that same reservation (40
 * candles against 40 of Context in Trade) squeezed the Context into a third of
 * a 360px pane, too thin to read, both while deciding and after a reveal that
 * resolved two candles in. So a compact pane fits the Context, the Forward
 * candles actually shown, and this short strip, re-fitting as the reveal or
 * the replay walks further. Candles narrow as more of the Forward is drawn,
 * which reads as the chart zooming out rather than as a jump.
 */
const COMPACT_FORWARD_BUDGET = 4;

/** Roughly half a label's rendered height, in pixels: how far the label's
 *  y-coordinate is kept from the pane's top/bottom edge (see `labeledLine`). */
const LABEL_MARGIN = 8;

/**
 * How far a top-anchored vertical marker's label sits below the pane's top
 * edge (see `labeledVerticalLine`): clears the "% from start" readout drawn
 * in the same corner (Chart's own JSX, top-right), which a plain LABEL_MARGIN
 * sits directly under. The decision point is the last Context candle, which
 * is exactly that top-right corner whenever little or no Forward is charted
 * yet — every Next-Candle Loop, and a trend Loop before its resolution walks
 * far — so without this the two would print on top of each other.
 */
const TOP_MARKER_CLEARANCE = 34;

/**
 * The same, while a `caption` is shown: the caption pushes the OHLC readout
 * down (see `CAPTION_CLEARANCE`), and the readout runs along the pane's top
 * edge past the decision point's x on any wide pane, so the marker's label
 * drops under both rather than printing through the readout's text.
 */
const CAPTIONED_MARKER_CLEARANCE = 50;

/**
 * A rough per-character width, in pixels, for a vertical marker's label chip
 * (`labeledVerticalLine`, `size: 11` monospace, see `labelStyle`), used only
 * to keep the chip from clipping against the pane's left/right edge (see
 * `LABEL_HORIZONTAL_PADDING` and the `align` switch below). KLineChart's
 * figure API hands back coordinates and pane bounding, not font metrics, so
 * this stays an estimate rather than a measured width; monospace makes that
 * estimate safe; erring wide (rather than measuring exactly) only costs a
 * few px of margin, never a clip.
 */
const LABEL_CHAR_WIDTH = 7;

/**
 * The label chip's own border and padding (`labelStyle`: 1px border, 5px
 * left/right padding), added to `LABEL_CHAR_WIDTH * text.length` to estimate
 * the chip's full rendered width.
 */
const LABEL_HORIZONTAL_PADDING = 12;

/**
 * The same estimate on the other axis: the chip's rendered height (`size: 11`
 * monospace plus its border and vertical padding), used only to tell whether
 * a label drawn above its anchor would fall off the top of the pane.
 */
const LABEL_LINE_HEIGHT = 16;

/**
 * Where the OHLC readout starts, from the pane's top, while a `caption` sits
 * above it: the caption's line height (`text-sm`, so the grid-line backing
 * box reads clearly) plus its own vertical padding (`py-0.5`, so that
 * backing box fully covers a dashed price line crossing its midline) plus a
 * small gap, with the caption itself sitting closer to the pane's top edge
 * (`top-1` rather than `top-2`).
 */
const CAPTION_CLEARANCE = 30;

/**
 * A horizontal line to draw at a price level (e.g. a barrier, a stop-loss), or
 * a vertical one to mark a candle by its position in the plotted series (e.g.
 * the reveal's decision point / resolution candle, GRA-124). Distinguished by
 * `value` (a price) vs `dataIndex` (a candle position) since the two draw
 * differently and never both apply to the same line. `tone` names a theme
 * token rather than a color, so the palette stays in `index.css` and the
 * caller never hardcodes hex. `label` is drawn next to the line (e.g. a
 * barrier's "+2.0%", or "Decision"); the caller decides its text, so this
 * stays as anonymized as whatever it passes in. A vertical marker's `anchor`
 * pins its label to the top or bottom of the pane (default "top") so two
 * markers sitting close together (a fast resolution) don't print their labels
 * on top of each other.
 *
 * A price line may also ask to be drawn `dashed` (the look the Last close
 * line wears: a reference the round is read against rather than a level
 * someone set), and to be `draggable`, i.e. moved vertically with the mouse. A
 * draggable line reports its new price through Chart's `onOverlayDrag`; the
 * caller stays the owner of the value, so nothing here decides what a drag
 * means.
 *
 * A price line's `compactLabel`, when given, replaces its `label` on a
 * phone-width pane (a barrier's bare "+2.0%" instead of "Upper +2.0%"), where
 * the full chips crowd a chart only a few hundred pixels wide.
 *
 * A vertical marker may also ask to be drawn `dotted` rather than dashed
 * (the look the Resolved marker wears, below: a shorter dash pattern reads
 * as a distinct mark from the Decision line's dashes even where the two
 * sit close together), and its `label` may be the empty string, which draws
 * no text figure at all, just the line (see `labeledVerticalLine`'s
 * `createPointFigures`).
 *
 * A third shape, `segment`, is a straight line between two (candle, price)
 * points rather than across the whole pane: the practice packs' hand-drawn
 * annotations (the upper edge of a flag, the path a round took to its
 * barrier), which are about a stretch of the chart and not about a level. Its
 * `label`, when given, is printed at the second point, so a line drawn left to
 * right names itself where it ends. Like a vertical marker it is never
 * draggable and never carries a price into the axis.
 */
export type OverlayLine =
  | {
      value: number;
      tone?: OverlayTone;
      label?: string;
      compactLabel?: string;
      dashed?: boolean;
      draggable?: boolean;
    }
  | {
      dataIndex: number;
      tone?: OverlayTone;
      label: string;
      anchor?: "top" | "bottom";
      dotted?: boolean;
    }
  | {
      segment: [
        { dataIndex: number; value: number },
        { dataIndex: number; value: number },
      ];
      tone?: OverlayTone;
      label?: string;
    };

/** Theme tokens an overlay may be tinted with (see `@theme` in index.css). */
export type OverlayTone = "up" | "down" | "muted" | "accent";

/**
 * A horizontal line that also carries a text label, registered once at module
 * load. KLineChart's own `priceLine` template prints the line's raw value, so
 * it can't be reused here: an app overlay's label must stay whatever the
 * caller passed in (a relative percent, not an absolute price) even after
 * reveal un-blanks the axis. The label lives in `extendData` rather than a
 * point, since it is text, not a price.
 */
registerOverlay({
  name: "labeledLine",
  totalStep: 2,
  // No point handle: a draggable line is grabbed anywhere along its length,
  // and the default dot would sit at the pane's left edge over the chip.
  needDefaultPointFigure: false,
  needDefaultXAxisFigure: true,
  needDefaultYAxisFigure: true,
  createPointFigures: ({ overlay, coordinates, bounding }) => {
    const label = typeof overlay.extendData === "string" ? overlay.extendData : "";
    const line = {
      type: "line",
      attrs: {
        coordinates: [
          { x: 0, y: coordinates[0].y },
          { x: bounding.width, y: coordinates[0].y },
        ],
      },
    };
    if (!label) return [line];
    // The pane autoscales to the visible candles, not to the barriers, so a
    // wide barrier (a volatile Context, or the Hard preset) can sit above or
    // below that range entirely, off-canvas. The label's y is clamped to stay
    // fully inside the pane either way, "middle"-anchored so it doesn't need
    // a separate top/bottom flip: a clamped label reads as "this barrier is
    // beyond what's shown, in this direction" rather than vanishing.
    const y = Math.min(
      Math.max(coordinates[0].y, LABEL_MARGIN),
      bounding.height - LABEL_MARGIN,
    );
    return [
      line,
      {
        type: "text",
        ignoreEvent: true,
        attrs: { x: 4, y, text: label, baseline: "middle" },
      },
    ];
  },
});

/**
 * The label chip for a vertical reveal marker (GRA-124: the decision point
 * and resolution candle), registered alongside `labeledLine`. Points by
 * `dataIndex` rather than `value`, so unlike a barrier it needs no price
 * (nothing here can leak an absolute price while anonymized). `extendData`
 * carries `{ text, anchor, clearance }` rather than a plain string like
 * `labeledLine`, since the label also needs to know which edge of the pane to
 * sit against, and how far below the top one (see `TOP_MARKER_CLEARANCE`).
 * The chip is normally centered on the line's x, but flips to hug the pane's
 * left or right edge instead when the marked candle sits close enough to one
 * that a centered chip would clip (the resolution candle is often the
 * newest, i.e. rightmost, one plotted; see `LABEL_CHAR_WIDTH`).
 *
 * Draws only the text chip, not the line itself (contrast `labeledLine`,
 * whose line and text are one overlay): KLineChart paints every overlay onto
 * a canvas layer composited on top of the candle pane's own, with `zLevel`
 * only ordering overlays against each other, never against the candles
 * (`OverlayView.drawImp` in klinecharts 9.8.12 never reads it) — so an
 * overlay line can't be sent under a candle body the way GRA-124 needs for a
 * candle that lands on the marker. The line is instead drawn by the
 * `appVerticalMarkers` indicator below, which shares the candles' own canvas
 * and so can. The chip stays here, and stays on top, since a label must
 * remain legible regardless of what it's pointing at.
 */
registerOverlay({
  name: "labeledVerticalLine",
  totalStep: 2,
  needDefaultPointFigure: true,
  needDefaultXAxisFigure: true,
  needDefaultYAxisFigure: true,
  createPointFigures: ({ overlay, coordinates, bounding }) => {
    const extend = overlay.extendData as
      | { text: string; anchor?: "top" | "bottom"; clearance?: number }
      | undefined;
    if (!extend?.text) return [];
    // Anchored top or bottom (the caller's choice) rather than always the
    // same edge, so a decision marker and a resolution marker sitting only a
    // candle or two apart (a fast resolution, or the Next-Candle Loops where
    // the answer is always exactly one candle past the decision point) print
    // their labels apart instead of stacked on top of each other.
    //
    // The label sits centered on the line's x, unlike `labeledLine`'s
    // horizontal labels (which sit beside their line): a vertical marker's
    // line is the thing being pointed at, so its chip reads clearest
    // straddling it rather than sitting to one side.
    const top = extend.anchor !== "bottom";
    const lineX = coordinates[0].x;
    const y = top ? (extend.clearance ?? TOP_MARKER_CLEARANCE) : bounding.height - LABEL_MARGIN;
    // The line's x can sit right at the pane's left or right edge (the
    // resolution candle is often the newest one plotted, i.e. the rightmost),
    // where a center-aligned chip would draw half off-canvas and clip. Rather
    // than clamp x itself (which would pull the chip away from the line it's
    // labeling), the chip switches to hug the near edge instead: right-aligned
    // against a right margin when centering would overflow right, left-aligned
    // against a left margin when it would overflow left, center otherwise.
    const halfWidth = (extend.text.length * LABEL_CHAR_WIDTH + LABEL_HORIZONTAL_PADDING) / 2;
    let x = lineX;
    let align: "left" | "center" | "right" = "center";
    if (lineX + halfWidth > bounding.width - LABEL_MARGIN) {
      align = "right";
      x = bounding.width - LABEL_MARGIN;
    } else if (lineX - halfWidth < LABEL_MARGIN) {
      align = "left";
      x = LABEL_MARGIN;
    }
    return [
      {
        type: "text",
        ignoreEvent: true,
        attrs: {
          x,
          y,
          text: extend.text,
          baseline: top ? "top" : "bottom",
          align,
        },
      },
    ];
  },
});

/**
 * A straight line between two points on the chart, with an optional label at
 * the far end, registered alongside the two above. KLineChart ships a
 * `segment` overlay, but its own draws endpoint handles (it is a drawing tool,
 * meant to be grabbed) and prints no text, and an app annotation is neither
 * grabbable nor nameless. So this is the same two-point line with the handles
 * dropped and a text figure added, which keeps every app overlay on the one
 * code path that reads `extendData` for its label.
 *
 * The label is anchored at the second coordinate and drawn just above it,
 * offset far enough to clear the line's own stroke; it prints nothing when
 * `extendData` carries no text, exactly as `labeledVerticalLine` does. An
 * annotation's far end is very often the newest candle plotted, i.e. hard
 * against the pane's right edge, and a flag drawn along the highs can sit
 * above the pane's top, so the chip hugs whichever edge it would otherwise
 * overflow, the same way `labeledVerticalLine`'s does.
 */
registerOverlay({
  name: "labeledSegment",
  totalStep: 3,
  // Same reason as `labeledLine`: an app annotation is a readout, not a
  // drawing, so it shows no grab handles.
  needDefaultPointFigure: false,
  needDefaultXAxisFigure: false,
  needDefaultYAxisFigure: false,
  createPointFigures: ({ overlay, coordinates, bounding }) => {
    if (coordinates.length < 2) return [];
    const label = typeof overlay.extendData === "string" ? overlay.extendData : "";
    const figures: Record<string, unknown>[] = [
      { type: "line", ignoreEvent: true, attrs: { coordinates } },
    ];
    if (label) {
      const end = coordinates[1];
      const halfWidth = (label.length * LABEL_CHAR_WIDTH + LABEL_HORIZONTAL_PADDING) / 2;
      let x = end.x;
      let align: "left" | "center" | "right" = "center";
      if (end.x + halfWidth > bounding.width - LABEL_MARGIN) {
        align = "right";
        x = bounding.width - LABEL_MARGIN;
      } else if (end.x - halfWidth < LABEL_MARGIN) {
        align = "left";
        x = LABEL_MARGIN;
      }
      // Above the endpoint by default, below it when that would put the chip
      // off the top of the pane or into the "% from start" readout drawn in
      // that same corner (a flag along the highs on a phone-height pane is
      // exactly that), so the label is never clipped or printed under it.
      const above = end.y - LABEL_MARGIN - LABEL_LINE_HEIGHT > TOP_MARKER_CLEARANCE;
      figures.push({
        type: "text",
        ignoreEvent: true,
        attrs: {
          x,
          y: above ? end.y - LABEL_MARGIN : end.y + LABEL_MARGIN,
          text: label,
          baseline: above ? "bottom" : "top",
          align,
        },
      });
    }
    return figures as never;
  },
});

/**
 * Name of a figure-less indicator that exists only to widen the candle
 * pane's y-axis range. KLineChart scales that axis to the visible candles
 * only (`YAxisImp.calcRange`), so a barrier or Entry/Stop/Target line drawn
 * beyond them (a barrier above every candle in a quiet Context, say) sits
 * off-canvas with only its label chip clamped to the pane edge visible.
 * `calcRange` also folds every indicator's `minValue`/`maxValue` on the pane
 * into the range it computes, which is the only lever KLineChart exposes for
 * this, so the app registers one with no visible footprint and drives those
 * two fields from the app overlays' actual prices (see the effect that
 * rebuilds `APP_GROUP`).
 *
 * Nothing about it is meant to be seen: no figures, an empty `shortName`,
 * and a `createTooltipDataSource` returning an empty name and no legends, so
 * the candle tooltip's indicator row (drawn by a `IndicatorTooltipView`
 * entirely separate from the app's own `candle.tooltip.custom`, see
 * `tooltipLegends`) never prints a row or shifts the OHLC readout for it.
 */
const RANGE_INDICATOR_NAME = "appRange";

/**
 * Pinned above `pricePrecision`'s ceiling (6, see chartData.ts) so this
 * indicator's own precision never becomes the tightest one on the pane and
 * rounds off a price label that would otherwise show one more decimal.
 */
const RANGE_INDICATOR_PRECISION = 8;

/**
 * (Re-)register the range indicator's template with a specific min/max baked
 * in. Registering, rather than updating an existing instance, is
 * deliberate: KLineChart 9.8.12's `overrideIndicator` (and `createIndicator`'s
 * own `minValue`/`maxValue` arguments, which are applied through the same
 * merge path) sets *both* fields through `setMinValue` — a copy-paste bug —
 * so a `maxValue` passed that way clobbers `minValue` instead of taking
 * effect. A freshly constructed instance reads both fields directly off the
 * template it was registered with, which isn't touched by that bug, so the
 * range is applied by re-registering the template and then creating a
 * bare-named (no per-call overrides) instance from it.
 */
function registerRangeIndicator(minValue: number, maxValue: number): void {
  registerIndicator({
    name: RANGE_INDICATOR_NAME,
    shortName: "",
    precision: RANGE_INDICATOR_PRECISION,
    figures: [],
    calc: () => [],
    createTooltipDataSource: () => ({ name: "", calcParamsText: "", icons: [], values: [] }),
    minValue,
    maxValue,
  });
}

/**
 * Name of a figure-less indicator that draws the app's vertical reveal
 * markers' lines (Decision, Resolved — GRA-124) UNDER the candles, so a
 * candle that lands on a marker keeps its body and wick legible instead of
 * being painted over. Unlike `RANGE_INDICATOR_NAME`, this one does draw
 * something, via `draw`: an indicator's canvas is the *candle pane's own*
 * (`IndicatorView.drawImp` runs in the same `updateMainContent` pass as
 * `CandleBarView`, both painting the pane's single `_mainCanvas`), and an
 * indicator with `zLevel < 0` is drawn there with
 * `ctx.globalCompositeOperation = 'destination-over'`, which only paints
 * pixels not already opaque — i.e. behind whatever candle bodies/wicks were
 * already drawn that pass. An *overlay*'s canvas, by contrast, is a second
 * one (`_overlayCanvas`) always composited on top of the pane's main canvas
 * regardless of `zLevel` (`DrawWidget`'s constructor appends it after the
 * main canvas; `OverlayView.drawImp` never reads `zLevel` at all), which is
 * why the line moved here instead of staying part of the `labeledVerticalLine`
 * overlay above — the label chip stays an overlay, since a chip must stay on
 * top of whatever it labels.
 */
const VERTICAL_MARKER_INDICATOR_NAME = "appVerticalMarkers";

/** One vertical marker's line, as drawn by the `appVerticalMarkers` indicator
 *  (see above): where (`dataIndex`), what color, and its dash pattern (the
 *  Decision line's dashes vs the Resolved marker's shorter dots). */
type VerticalMarkerSpec = { dataIndex: number; color: string; dash: number[] };

/**
 * (Re-)register the vertical-marker indicator's template with a specific set
 * of markers baked in, then create a bare-named instance from it — the same
 * register-then-create dance `registerRangeIndicator` uses, and for the same
 * reason: a fresh instance reads its `draw` closure straight off the
 * template it names, so re-registering is how new marker positions/colors
 * actually take effect on the next `createIndicator` call below.
 */
function registerVerticalMarkerIndicator(markers: VerticalMarkerSpec[]): void {
  registerIndicator({
    name: VERTICAL_MARKER_INDICATOR_NAME,
    shortName: "",
    // Same ceiling as `appRange`, for a subtler reason than a label's
    // decimals: KLineChart's `YAxis.calcRange` takes the *lowest* precision
    // of any indicator on the candle pane and treats a price range narrower
    // than 10^-precision as flat, padding it by four whole units either
    // side. A `0` here made every sub-1.00 range (a 1.30 currency future)
    // render as a hairline between -3 and 6.
    precision: RANGE_INDICATOR_PRECISION,
    figures: [],
    calc: () => [],
    createTooltipDataSource: () => ({ name: "", calcParamsText: "", icons: [], values: [] }),
    // Negative: painted behind the candles already drawn this pass (see the
    // doc comment on `VERTICAL_MARKER_INDICATOR_NAME`).
    zLevel: -1,
    draw: ({ ctx, bounding, xAxis }) => {
      ctx.save();
      ctx.lineWidth = 1;
      for (const marker of markers) {
        const x = xAxis.convertToPixel(marker.dataIndex);
        ctx.beginPath();
        ctx.strokeStyle = marker.color;
        ctx.setLineDash(marker.dash);
        ctx.moveTo(x, 0);
        ctx.lineTo(x, bounding.height);
        ctx.stroke();
      }
      ctx.restore();
      // Skip the default figure-driven draw: there are no figures anyway
      // (see the empty `figures`/`calc` above), but the return value is what
      // tells KLineChart this indicator drew itself.
      return true;
    },
  });
}

/** A cache key for `themeFont()`'s result in the Chart component's per-mount
 * `themeColorCache` map (below); a `--color-<name>` lookup can never collide
 * with it. */
const FONT_CACHE_KEY = "__font-mono__";

/**
 * Read a `--color-<name>` custom property's current value: KLineChart draws
 * on canvas, which cannot parse `var(--color-up)`. Returns undefined when the
 * property is unset (jsdom, before styles load) so a caller can fall back to
 * the library's own default rather than drawing with an empty string.
 */
function themeColor(name: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(`--color-${name}`)
    .trim();
  return value === "" ? undefined : value;
}

/**
 * The chip look for an app overlay's label, matching the drawing-tool and
 * indicator chips above the chart rather than KLineChart's default badge (a
 * solid fill with white sans-serif text, which reads as another product's
 * widget dropped onto the pane). Monospace text in the line's own color on
 * the page background, inside a thin border of that same color: the label
 * still pairs with its line at a glance, just quietly.
 */
/**
 * `bg`/`font` are injectable so a caller redrawing many overlays in one pass
 * (the Chart component's own overlay effect) can read `--color-bg` and
 * `--font-mono` once per mount and pass the cached values through, instead
 * of this function re-reading both via `getComputedStyle` for every overlay.
 */
function labelStyle(
  color: string,
  bg: string | undefined = themeColor("bg"),
  font: string | undefined = themeFont(),
): Partial<TextStyle> {
  return {
    // Fill and stroke: the default "fill" ignores the border fields below.
    style: PolygonType.StrokeFill,
    color,
    family: font ?? "monospace",
    size: 11,
    weight: "normal",
    backgroundColor: bg ?? "#111112",
    borderColor: color,
    borderSize: 1,
    borderRadius: 3,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 2,
    paddingBottom: 2,
  };
}

/** The app's `--font-mono` stack for canvas text (see `themeColor` for why). */
function themeFont(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const value = getComputedStyle(document.documentElement).getPropertyValue("--font-mono").trim();
  return value === "" ? undefined : value;
}

/**
 * The same quiet chip look as `labelStyle`, for KLineChart's own chrome
 * (crosshair value/date bubbles, the last-price tag on the y-axis) rather
 * than an app overlay's label: monospace, `--color-surface` behind
 * `--color-muted` text with a 1px `--color-grid` border, instead of
 * KLineChart's default filled grey box in its own sans-serif (which read as
 * another product's widget dropped onto the pane, the same complaint
 * `labelStyle` exists to fix for barrier/marker chips). Kept as a distinct
 * helper (rather than reusing `labelStyle`) since these chips are neutral
 * chrome, not tinted to a line's tone: surface/grid instead of a color
 * parameter.
 *
 * `paddingTop`/`paddingBottom` are 6, not the 2 `labelStyle` uses, so the
 * chip's total height (paddingTop + size + paddingBottom = 23) matches the
 * x/y axis strip's own height. That is not cosmetic: KLineChart draws the
 * x-axis crosshair date chip anchored to the top of the axis strip
 * (`CrosshairVerticalLabelView.getTextAttrs` returns `y: 0, baseline:
 * 'top'`), while the tick date label beneath it is drawn lower in that same
 * strip (`axisLine.size + tickLine.length + tickText.marginStart`, the
 * default sum is 8px down) and is tall enough to reach close to the strip's
 * bottom edge. At the old height of 15 the chip's background rect stopped
 * short of that, so the last few pixels of the tick glyphs poked out below
 * the chip's border, reading as a transparent background even though
 * `backgroundColor` was opaque the whole time: the fill was solid, it just
 * didn't reach far enough down to cover what was underneath it. Sizing the
 * chip to the full strip height covers the tick label completely regardless
 * of exact font metrics, on both axes (the y-axis chip only grows slightly
 * taller around the cursor row, which is harmless there since that label
 * isn't anchored to a shared band the way the x-axis one is).
 */
function axisChipStyle(): Partial<TextStyle> {
  return {
    style: PolygonType.StrokeFill,
    color: themeColor("muted") ?? "#8a8d91",
    family: themeFont() ?? "monospace",
    size: 11,
    backgroundColor: themeColor("surface") ?? "#1a1a1c",
    borderColor: themeColor("grid") ?? "#2a2a2d",
    borderSize: 1,
    borderRadius: 2,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 6,
    paddingBottom: 6,
  };
}

/**
 * Chart chrome dyed from the theme tokens, independent of `revealed` /
 * `compact` (GRA-125), so it is set once at init rather than recomputed on
 * every anonymization re-render:
 *
 * - Grid lines and axis chrome use `--color-grid`, a token dimmer than any
 *   text in the app, so the grid reads as a structural cue behind the
 *   candles rather than competing with them (klinecharts' own default grid
 *   color is a near-white made for a light background, which drew brighter
 *   than everything else on this dark one).
 * - Axis tick text uses `--color-muted`, the same token the rest of the
 *   app's quiet-but-legible text uses, so a revealed axis label reads at the
 *   same contrast as everything else that isn't the candles.
 * - Candle bars and the last-price mark use `--color-up` / `--color-down`,
 *   the app's one source of truth for direction, instead of klinecharts' own
 *   green/red — keeping the candles themselves the brightest thing on the
 *   chart and consistent with every other place direction is drawn.
 */
function baseChartStyles() {
  const grid = themeColor("grid");
  const muted = themeColor("muted");
  const surface = themeColor("surface");
  const font = themeFont();
  const up = themeColor("up");
  const down = themeColor("down");
  const accent = themeColor("accent");
  const bg = themeColor("bg");
  // The crosshair value/date bubble and the last-price y-axis tag both want
  // the same quiet chip (`axisChipStyle`), not KLineChart's own filled grey
  // box in its default sans-serif (see `axisChipStyle`'s own doc comment).
  const chip = axisChipStyle();
  const bar =
    up && down
      ? {
          upColor: up,
          downColor: down,
          upBorderColor: up,
          downBorderColor: down,
          upWickColor: up,
          downWickColor: down,
        }
      : {};
  return {
    grid: {
      horizontal: grid ? { color: grid } : {},
      vertical: grid ? { color: grid } : {},
    },
    xAxis: {
      axisLine: grid ? { color: grid } : {},
      tickLine: grid ? { color: grid } : {},
      tickText: {
        ...(muted ? { color: muted } : {}),
        ...(font ? { family: font } : {}),
        size: 11,
      },
    },
    yAxis: {
      axisLine: grid ? { color: grid } : {},
      tickLine: grid ? { color: grid } : {},
      tickText: {
        ...(muted ? { color: muted } : {}),
        ...(font ? { family: font } : {}),
        size: 11,
      },
    },
    // The value/date bubble that follows the crosshair, given the same
    // `axisChipStyle` chip as the last-price tag below rather than
    // KLineChart's own filled grey box (see that helper's doc comment). Only
    // `text` is styled here; `show` is `anonymizationStyles`' concern (it
    // toggles with `revealed`, not with the theme).
    crosshair: {
      horizontal: { text: chip },
      vertical: { text: chip },
    },
    candle: {
      bar,
      // The OHLC readout in the app's own monospace and quiet text color,
      // not KLineChart's Helvetica in near-white: it sits in the pane's
      // top-left corner and read as a foreign widget otherwise.
      tooltip: {
        text: {
          ...(muted ? { color: muted } : {}),
          ...(font ? { family: font } : {}),
          size: 12,
        },
      },
      priceMark: {
        // The built-in last-price mark keeps only its axis label (its dashed
        // line is replaced by the "Last close" app overlay, see `startLine`,
        // and a direction-tinted line next to Stop/Target reads as a third
        // barrier). That label's `text.backgroundColor` is ignored by
        // KLineChart's own `CandleLastPriceLabelView` (it always paints the
        // tag's fill from `last.upColor`/`downColor`/`noChangeColor`
        // instead), which is why these are set to the app's surface color
        // (the chip's background) rather than a direction color: a
        // direction-tinted tag next to Stop/Target reads as a third barrier,
        // the same reason the line itself is hidden below. The text itself
        // (`chip`, muted/monospace/bordered) is what actually makes it read
        // as one of the app's own chips instead of a solid grey block.
        last: {
          line: { show: false },
          ...(surface ? { upColor: surface, downColor: surface, noChangeColor: surface } : {}),
          text: chip,
        },
        // The high/low arrow-and-label marks, sized to match the OHLC
        // tooltip (`size: 12` above) rather than KLineChart's smaller
        // default, per request: bigger, not smaller, and in the app's own
        // monospace/muted color instead of its default sans-serif. Both
        // marks' `_drawMark` implementation uses a single `color` field for
        // the arrow line AND the label text together (there is no separate
        // line color field on this style), so setting `color` here already
        // covers "keep the little arrow line in the same muted colour"
        // without any extra field.
        high: {
          ...(muted ? { color: muted } : {}),
          ...(font ? { textFamily: font } : {}),
          textSize: 12,
        },
        low: {
          ...(muted ? { color: muted } : {}),
          ...(font ? { textFamily: font } : {}),
          textSize: 12,
        },
      },
    },
    // The pane divider between the candle pane and an indicator sub-pane
    // (RSI/MACD/VOL): `--color-grid`, the same structural line the
    // candle/axis grid already uses, instead of KLineChart's own near-white
    // default.
    separator: grid ? { color: grid } : {},
    // The indicator legend (MA/EMA/BOLL/RSI/MACD/VOL's name + values, top-left
    // of whichever pane the indicator lives on): the app's own monospace/muted
    // text instead of KLineChart's default sans-serif, and shown only while
    // the crosshair is over that pane (`FollowCross`) rather than always —
    // Marco's taste is minimal, and a legend that is always on competes with
    // the OHLC readout and the barrier chips in the same corner (see the
    // per-instance `offsetTop` in `indicatorStyles` for what keeps it clear
    // of both even while it is shown).
    indicator: {
      tooltip: {
        showRule: TooltipShowRule.FollowCross,
        text: {
          ...(muted ? { color: muted } : {}),
          ...(font ? { family: font } : {}),
          size: 11,
        },
      },
    },
    // Drawing-tool overlays (line/hline/fib): set once, here, as the chart's
    // own default rather than per-`createOverlay` call, so every overlay
    // gets it regardless of which call site created it — including ones
    // restored from persisted drawings (see the drawing-persistence path and
    // `onDrawingChange`), which never pass a `styles` object of their own.
    // `accent` for the line and its handles (KLineChart's own default is a
    // bright library blue that reads as another product's tool), the same
    // quiet chip `axisChipStyle` gives the axis/crosshair chrome for the
    // overlay's text label (muted text on `surface`, a `grid` border) rather
    // than KLineChart's solid-fill badge.
    overlay: {
      point: {
        ...(accent ? { color: accent, activeColor: accent } : {}),
        ...(bg ? { borderColor: bg, activeBorderColor: bg } : {}),
      },
      line: {
        ...(accent ? { color: accent } : {}),
        size: 1,
      },
      text: chip,
    },
  };
}

/**
 * Per-instance style overrides for the built-in KLineChart indicators
 * (MA/EMA/BOLL/RSI/MACD/VOL), dyed from the same theme tokens `baseChartStyles`
 * uses instead of the library's own purple/orange/blue/magenta lines and
 * teal/crimson volume bars. Passed to `createIndicator` as the `styles` field
 * on its object form (`{ name, styles }`) rather than the bare kline name
 * string, so each indicator instance carries its own line/bar colors.
 *
 * `lines`/`bars` entries cycle by index modulo the array length (KLineChart's
 * own `eachFigures`), so a single-entry array (MA, EMA, RSI) tints every one
 * of that indicator's lines the same color, while BOLL's three entries land
 * one-for-one on its up/mid/down figures (in that order) and MACD's two land
 * on its dif/dea lines.
 */
/**
 * A `lines[]`/`bars[]` entry, fully populated: `createIndicator`'s per-
 * instance `styles` replaces the library's whole default array rather than
 * merging into it field-by-field (see `eachFigures`'s `formatValue(styles,
 * 'lines', ...)`, which reads `styles.lines` outright when present), so a
 * sparse `{ color }` entry would leave `size`/`dashedValue`/`smooth` (or, for
 * a bar, `borderSize`/`borderStyle`) undefined instead of falling back to
 * KLineChart's own defaults the way `baseChartStyles`' chart-wide styles do.
 */
function indicatorLine(color: string): SmoothLineStyle {
  return { style: LineType.Solid, size: 1, color, dashedValue: [2, 2], smooth: false };
}

function indicatorBar(up: string, down: string, noChange: string): IndicatorPolygonStyle {
  return {
    style: PolygonType.Fill,
    borderSize: 1,
    borderStyle: LineType.Solid,
    borderDashedValue: [2, 2],
    upColor: up,
    downColor: down,
    noChangeColor: noChange,
  };
}

/**
 * What `indicatorStyles` actually builds: full `lines`/`bars` entries (see
 * `indicatorLine`/`indicatorBar`) but only the one `tooltip` field this app
 * ever overrides (`offsetTop`, see `MAIN_PANE_INDICATOR_TOOLTIP_CLEARANCE`),
 * not the rest of `IndicatorTooltipStyle`'s many fields KLineChart's own
 * defaults already cover. Narrower than `Partial<IndicatorStyle>` for that
 * reason; cast to it at the `createIndicator` call site, where `styles`
 * really does accept a partial replace of each field it names (see
 * `indicatorLine`'s doc comment on why each `lines`/`bars` entry itself
 * still has to be fully populated).
 */
type IndicatorStyleOverride = {
  lines?: SmoothLineStyle[];
  bars?: IndicatorPolygonStyle[];
  tooltip?: { offsetTop?: number };
};

function indicatorStyles(id: IndicatorId): IndicatorStyleOverride {
  const muted = themeColor("muted") ?? "#8a8d91";
  const dim = themeColor("dim") ?? "#7d8084";
  const accent = themeColor("accent") ?? "#e2b714";
  const up = themeColor("up") ?? "#4caf50";
  const down = themeColor("down") ?? "#ff5252";
  // Clears the OHLC readout and the barrier/marker chips in the candle
  // pane's top-left corner (see item 3 of GRA's chart-theming pass): only
  // the three main-pane indicators (MA/EMA/BOLL, see `MAIN_PANE_INDICATORS`)
  // share that pane with the readout and chips, so only those get the extra
  // clearance; RSI/MACD/VOL live on their own sub-pane with nothing else
  // drawn there, so they keep the library's own small offset.
  const mainPaneTooltip = { offsetTop: MAIN_PANE_INDICATOR_TOOLTIP_CLEARANCE };
  switch (id) {
    case "sma":
      return { lines: [indicatorLine(muted)], tooltip: mainPaneTooltip };
    case "ema":
      return { lines: [indicatorLine(accent)], tooltip: mainPaneTooltip };
    case "bbands":
      // Figure order is up, mid, down (see klinecharts' `bollingerBands`):
      // the band edges dimmer than the middle line, so the middle reads as
      // the actual moving average and the band as context around it.
      return {
        lines: [indicatorLine(dim), indicatorLine(muted), indicatorLine(dim)],
        tooltip: mainPaneTooltip,
      };
    case "rsi":
      return { lines: [indicatorLine(accent)] };
    case "macd":
      return {
        lines: [indicatorLine(muted), indicatorLine(accent)],
        bars: [indicatorBar(up, down, muted)],
      };
    case "vol":
      return {
        lines: [indicatorLine(muted)],
        bars: [indicatorBar(up, down, muted)],
      };
  }
}

/**
 * How far the indicator legend sits from the candle pane's top edge, for the
 * three main-pane indicators (MA/EMA/BOLL) only: below both the OHLC
 * readout (whose own clearance is `CAPTIONED_MARKER_CLEARANCE`'s caption-
 * aware `CAPTION_CLEARANCE`/6, see `anonymizationStyles`) and the readout's
 * own single row of text, so hovering with a caption, the OHLC tooltip AND
 * an indicator on all reads as one stacked corner instead of two things
 * fighting for the same pixels. Deliberately sized for the worst case
 * (captioned) rather than switching with `revealed`/`caption`, since a
 * per-instance indicator style is set once at `createIndicator` time and
 * isn't recomputed when those change — a little extra empty space above an
 * uncaptioned legend costs nothing, but a legend that only clears the
 * readout half the time would still collide the other half.
 */
const MAIN_PANE_INDICATOR_TOOLTIP_CLEARANCE = CAPTIONED_MARKER_CLEARANCE + 22;

/** RSI's own overbought/oversold thresholds; not a KLineChart concept, drawn
 *  by `drawRsiReferenceLines` below. */
const RSI_REFERENCE_LEVELS = [30, 70];

/**
 * RSI's 30/70 overbought/oversold reference lines, in `--color-grid` (the
 * same structural, dimmer-than-any-text line the candle grid itself uses) —
 * KLineChart's built-in RSI has no such lines of its own. Passed as the
 * `draw` field on the RSI's `createIndicator` call: it draws these first,
 * then returns `false` so KLineChart still runs its own figure-driven draw
 * afterward for the RSI line(s) themselves (see `IndicatorView.drawImp`'s
 * `isCover` check — only a truthy return skips that default draw).
 */
function drawRsiReferenceLines({ ctx, bounding, yAxis }: IndicatorDrawParams): boolean {
  const grid = themeColor("grid") ?? "#2a2a2d";
  ctx.save();
  ctx.lineWidth = 1;
  ctx.strokeStyle = grid;
  ctx.setLineDash([2, 2]);
  for (const level of RSI_REFERENCE_LEVELS) {
    const y = yAxis.convertToPixel(level);
    if (y < 0 || y > bounding.height) continue;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(bounding.width, y);
    ctx.stroke();
  }
  ctx.restore();
  return false;
}

/** Imperative handle for the TA workbench (GRA-83). */
export type ChartHandle = {
  /** Start an interactive drawing overlay for the given tool (user places the
   *  points). Translated to KLineChart's overlay name at this boundary. */
  startDrawing: (toolId: DrawingToolId) => void;
  /** Remove all user drawings (keeps app overlays like barriers). */
  clearDrawings: () => void;
};

export type ChartProps = {
  /** The Context candles (shown anonymized). */
  context: Candle[];
  /** The Forward candles, appended on reveal. */
  forward?: Candle[];
  /** When true, show the Forward and the true price labels. */
  revealed?: boolean;
  /** Horizontal price lines and vertical candle markers to draw; their
   *  meaning is the caller's concern. */
  overlays?: OverlayLine[];
  /**
   * Called while one of the `overlays` marked `draggable` is dragged, with the
   * line it belongs to and the price it now sits at, and once more when the
   * drag ends. Chart never moves a value by itself: the caller owns the
   * number and is expected to hand the moved line straight back in `overlays`,
   * so the field, the summary and the line can never disagree.
   */
  onOverlayDrag?: (overlay: OverlayLine, value: number) => void;
  /** Indicator ids to show (ours; translated to KLineChart's names at this
   *  boundary, see chartVocabulary.ts). */
  indicators?: IndicatorId[];
  /** Called with the tool that is armed for drawing, and with null once the
   *  drawing is placed, removed, or cleared — so the toolbar can show which
   *  tool is waiting for the player's clicks (GRA-83). */
  onDrawingChange?: (toolId: DrawingToolId | null) => void;
  /** Override ms between Forward candles on reveal; 0 shows them at once. When
   *  omitted, the step is derived from revealDurationMs so the reveal stays fast
   *  regardless of how many Forward candles there are. */
  revealStepMs?: number;
  /** Target total reveal duration (spec §9 — keep the loop fast). */
  revealDurationMs?: number;
  /** Called once the reveal animation has plotted every Forward candle (or
   *  immediately, when the step is instant). Lets the caller offer
   *  step-through controls only once there is a settled round to step through
   *  (GRA-136), rather than racing the animation. */
  onRevealComplete?: () => void;
  /**
   * How many Forward candles the reveal animation steps to before stopping
   * and firing `onRevealComplete` (GRA-147): the round's actual resolution
   * (a barrier touch, a Trade's fill candle, the answer candle), not
   * necessarily the whole Forward. A Trade that resolved on the first candle
   * opens there, with the rest of the path not yet drawn, instead of
   * animating through candles that happened after the trade was already
   * closed. Clamped to `[0, forward.length]`; omitted (or past the Forward)
   * defaults to `forward.length`, the old "animate the whole thing" behavior
   * every non-stepping caller still wants.
   */
  revealTo?: number;
  /**
   * Overrides how many Forward candles are shown, once the reveal animation
   * has finished, so the caller can step back and forth through the Forward
   * one candle at a time (GRA-136). `null`/`undefined` defers to the
   * animation's own count, which is what every caller that doesn't step
   * wants; a caller driving this is expected to wait for `onRevealComplete`
   * first, so it never fights the animation mid-run.
   */
  forwardStepOverride?: number | null;
  className?: string;
  /**
   * A line of text for the pane's top-left corner, above the OHLC readout:
   * the Instrument's name and date range, once revealed. Shown only while
   * `revealed`, whatever is passed, since the Instrument's identity is part
   * of what anonymization hides.
   */
  caption?: string;
  /**
   * Drop the "from chart start" sub-label under the percent readout. The
   * Review dialog passes this: there the whole Round is already known, so
   * the reference point reads as noise rather than a needed caveat.
   */
  hidePercentReference?: boolean;
};

const DEFAULT_REVEAL_DURATION_MS = 600;

// Overlay groups: app-managed (barriers/SL-TP) vs user drawings. Grouping lets
// data updates redraw barriers without wiping the player's TA drawings, and lets
// "clear" remove only drawings (spec §8: drawings persist through reveal).
const APP_GROUP = "app";

/**
 * Default for the `forward` prop. A literal `[]` default would be a new array
 * on every render, and it is a dependency of the effect that re-applies the
 * data, styles and app overlays, so every state change (arming a tool, say)
 * would redo all of that for nothing.
 */
const NO_FORWARD: Candle[] = [];
/** The same, for the `overlays` prop (see `NO_FORWARD`). */
const NO_OVERLAYS: OverlayLine[] = [];
const DRAWING_GROUP = "drawing";

/**
 * The id a draggable app overlay is created under, stable across redraws so
 * its price can be pushed into the live overlay instead of the whole app
 * group being recreated. Keyed by the label, which is what tells one line
 * from another (Stop from Target), with the position as a fallback for the
 * unlabeled case.
 */
function draggableId(label: string | undefined, index: number): string {
  return `${APP_GROUP}-drag-${label ?? index}`;
}

// Overlay indicators that belong on the price (candle) pane rather than a
// separate sub-pane. RSI/MACD/VOL get their own pane by default.
const MAIN_PANE_INDICATORS = new Set<IndicatorId>(["sma", "ema", "bbands"]);
const CANDLE_PANE_ID = "candle_pane";

/**
 * Styles that enforce anonymization while `revealed` is false: hide the absolute
 * price (y-axis) AND date (x-axis) tick labels, the crosshair value/date
 * bubbles, the candle OHLC tooltip, and the high/low price mark labels on the
 * candle pane itself, so no absolute price or date is readable, even on
 * hover, until reveal (spec §0 "anonymization includes price"; dates hidden
 * while deciding). The last-price mark keeps its dashed line in both states
 * (it carries no absolute price by itself), but its numeric label is gated the
 * same way: KLineChart shows it by default, and only the collapsed y-axis
 * happens to clip it while anonymized, so the gate makes the invariant
 * explicit instead of incidental.
 *
 * `compact` (a phone-width pane) keeps the OHLC tooltip off even after reveal:
 * its six fields wrap over the candles at that width, hiding the chart the
 * player is meant to read. For the same reason it drops the high/low marks,
 * which land on the caption and marker chips at that width, and the vertical
 * grid, which the date axis already stands in for.
 */
function anonymizationStyles(revealed: boolean, compact = false, captioned = false) {
  // `size: 'auto' | 0` is what actually reclaims the axis strip: KLineChart's
  // layout (`getAutoSize`) returns an explicit numeric size immediately,
  // bypassing the show-flag-driven auto calculation it otherwise runs, so
  // `0` here yields a true zero-width/zero-height axis rather than an empty
  // reserved column. `show`/`axisLine`/`tickLine`/`tickText` are hidden too
  // so no stray line renders at the now-collapsed edge.
  const axis = revealed
    ? { show: true, size: "auto" as const, axisLine: { show: true }, tickLine: { show: true }, tickText: { show: true } }
    : { show: false, size: 0, axisLine: { show: false }, tickLine: { show: false }, tickText: { show: false } };
  return {
    xAxis: axis,
    yAxis: axis,
    grid: { vertical: { show: !compact } },
    crosshair: {
      horizontal: { text: { show: revealed } },
      vertical: { text: { show: revealed } },
    },
    candle: {
      tooltip: {
        showRule: revealed && !compact ? TooltipShowRule.Always : TooltipShowRule.None,
        // Under the caption when there is one (see `caption`), else at the
        // library's default 6px from the pane's top.
        offsetTop: captioned ? CAPTION_CLEARANCE : 6,
      },
      priceMark: {
        high: { show: revealed && !compact },
        low: { show: revealed && !compact },
        last: { text: { show: revealed } },
      },
    },
  };
}

/**
 * The OHLC readout's legends, replacing KLineChart's default row. Two things
 * the default gets wrong for this bank: it prints a clock time next to the
 * date, and every Round here is daily or coarser with midnight-UTC stamps, so
 * the time was always "00:00" (or the viewer's UTC offset, which is worse:
 * "01:00" says nothing true about the candle); and it prints "Volume: 0" for
 * the Instruments whose feed carries no volume, so that column is dropped
 * when the candle has none. Prices are formatted to the pane's precision
 * with thousands separators, as the default did.
 */
function tooltipLegends(precision: number): CandleTooltipCustomCallback {
  const price = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
  return ({ current }) => {
    const legends = [
      { title: "", value: new Date(current.timestamp).toISOString().slice(0, 10) },
      { title: "Open: ", value: price.format(current.open) },
      { title: "High: ", value: price.format(current.high) },
      { title: "Low: ", value: price.format(current.low) },
      { title: "Close: ", value: price.format(current.close) },
    ];
    if (current.volume) {
      legends.push({ title: "Volume: ", value: new Intl.NumberFormat("en-US").format(current.volume) });
    }
    return legends;
  };
}

/**
 * The dashed "Last close" line at the decision price (the last Context
 * close), standing in for KLineChart's own last-price mark. That mark was
 * tinted by the last candle's direction, so next to a red Stop and a green
 * Target it read as a third barrier (a mystery red dotted line), and it
 * tracked the newest candle through the reveal, so the reference the player
 * guessed from slid away under them. This one is labeled, neutral, and
 * fixed: the Forward is read against it. Named "Last close" rather than
 * "Start" so it can't be read as the same reference the chart's own "% from
 * chart start" readout measures from (the first *plotted* candle, which
 * isn't always the decision point — a wider Context-length setting plots
 * candles before it). It is skipped when an app overlay already sits on
 * that price, as the Trade Loop's Entry line always does.
 */
function startLine(context: Candle[], overlays: OverlayLine[] | undefined): OverlayLine | null {
  const start = context.at(-1)?.close;
  if (start === undefined) return null;
  if ((overlays ?? []).some((o) => "value" in o && o.value === start)) return null;
  return { value: start, tone: "muted", label: "Last close" };
}

/**
 * A dotted "Resolved" vertical marker at the candle a resolved round actually
 * ended on: the decision point plus however many Forward candles it took to
 * resolve (`revealTo`), the same target the reveal animation above steps
 * to, defaulting the same way it does (the whole Forward) when the caller
 * doesn't pass one. Drawn only once the round is actually revealed with a
 * Forward to resolve against, so it never appears during the decision phase;
 * it disappears the moment a fresh round replaces the Context/Forward, since
 * the app group these markers live in is cleared and rebuilt on every data
 * update (see the effect below). Anchored to the bottom of the pane (unlike
 * the Decision marker's top anchor), so a fast resolution or a Next-Candle
 * Loop's always-one-candle-later resolution doesn't stack its chip on the
 * Decision chip; dotted rather than dashed so it also reads apart from the
 * Decision line at a glance.
 */
function resolutionMarker(
  context: Candle[],
  forward: Candle[],
  revealed: boolean,
  revealTo: number | undefined,
): OverlayLine | null {
  if (!revealed || forward.length === 0) return null;
  const target = Math.max(0, Math.min(revealTo ?? forward.length, forward.length));
  return {
    dataIndex: context.length - 1 + target,
    tone: "accent",
    label: "Resolved",
    anchor: "bottom",
    dotted: true,
  };
}

export const Chart = forwardRef<ChartHandle, ChartProps>(function Chart(props, ref) {
  const {
    context,
    forward = NO_FORWARD,
    revealed = false,
    overlays,
    indicators,
    revealStepMs,
    revealDurationMs = DEFAULT_REVEAL_DURATION_MS,
    onRevealComplete,
    revealTo,
    forwardStepOverride,
    onDrawingChange,
    onOverlayDrag,
    className,
    caption,
    hidePercentReference = false,
  } = props;

  // Held in a ref so the imperative handle (and the chart's own overlay
  // callbacks, which outlive a render) never need rebuilding when the parent
  // passes a fresh closure.
  const onDrawingChangeRef = useRef(onDrawingChange);
  onDrawingChangeRef.current = onDrawingChange;

  // Same pattern as onDrawingChangeRef: the reveal-driving effect below only
  // depends on revealed/forward.length/timing, not on the caller's callback
  // identity, so it must read the latest one through a ref rather than
  // restarting the animation whenever the parent re-renders with a fresh
  // closure.
  const onRevealCompleteRef = useRef(onRevealComplete);
  onRevealCompleteRef.current = onRevealComplete;

  // Same pattern again: the drag callbacks are handed to KLineChart once, when
  // the overlay is created, and outlive every render after that.
  const onOverlayDragRef = useRef(onOverlayDrag);
  onOverlayDragRef.current = onOverlayDrag;

  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<KLineChartInstance | null>(null);
  // Track live indicator pane ids by domain id so they can be removed cleanly.
  const indicatorPanes = useRef<Map<IndicatorId, string>>(new Map());

  // `themeColor` reads a CSS custom property via `getComputedStyle`, which
  // forces a synchronous style/layout recalculation whenever it lands right
  // after a DOM write. The overlay-redraw effect below calls it once per
  // toned line, every time a Round's candles change (a guess, a reveal step,
  // a replay scrub), interleaved with the chart's own canvas writes. The app
  // has exactly one theme, so a token's value cannot change across the life
  // of this mounted Chart: read each name at most once per mount here and
  // reuse it, instead of re-reading it on every overlay on every redraw.
  const themeColorCache = useRef<Map<string, string | undefined>>(new Map());
  const cachedThemeColor = useCallback((name: string) => {
    const cache = themeColorCache.current;
    if (!cache.has(name)) cache.set(name, themeColor(name));
    return cache.get(name);
  }, []);
  const cachedToneColor = useCallback(
    (tone: OverlayTone | undefined) => (tone ? cachedThemeColor(tone) : undefined),
    [cachedThemeColor],
  );
  // Same reasoning as themeColorCache, for the `--font-mono` stack
  // `labelStyle` reads; a key no real `--color-<name>` lookup can collide
  // with, so it shares the same cache map.
  const cachedThemeFont = useCallback(() => {
    const cache = themeColorCache.current;
    if (!cache.has(FONT_CACHE_KEY)) cache.set(FONT_CACHE_KEY, themeFont());
    return cache.get(FONT_CACHE_KEY);
  }, []);

  // How many Forward candles are currently shown (grows during the reveal).
  const [shownForward, setShownForward] = useState(0);

  // The count actually rendered: the animation's own count, unless the caller
  // is stepping manually (forwardStepOverride set, GRA-136), and never past
  // what revealed actually allows — an override left stale by a parent that
  // hasn't yet reset it on a fresh Round must not leak a stray Forward candle
  // in for a beat before that reset lands.
  const displayedForward = revealed ? (forwardStepOverride ?? shownForward) : 0;

  // Whether a drawing tool is armed and waiting for the player's points. Drives
  // the container's touch-action (see chartGestures.ts): a drawing stroke must
  // not be taken away by the page scroll that a drag otherwise means.
  const [drawingArmed, setDrawingArmed] = useState(false);
  // The y-axis's width, re-measured after every style change: it collapses to
  // nothing while anonymized (no tick text) and opens to the width of its
  // price labels at reveal, and the "% from start" readout in the top-right
  // corner has to move left with it rather than print over the labels.
  const [axisWidth, setAxisWidth] = useState(0);

  // Whether the pane is too narrow for the OHLC tooltip. State, not a ref, so
  // the styles effect below re-runs when the container crosses the threshold.
  const [compact, setCompact] = useState(false);

  // The live chart instance, held in state as well as in the ref so the data /
  // styles / indicator effects below re-run against a *new* instance. Without
  // that, a re-init that leaves every prop untouched — React Fast Refresh in
  // dev, StrictMode's double mount — leaves the freshly-initialized chart with
  // no data at all, which also drops the anonymization styles and exposes the
  // real y-axis prices until a hard reload.
  const [chart, setChart] = useState<KLineChartInstance | null>(null);

  // Latest candle counts, read by the resize observer and the fit effect
  // below without either needing to be recreated when they change.
  const lengthsRef = useRef({ contextLen: context.length, forwardLen: forward.length, shown: displayedForward, revealed });
  lengthsRef.current = { contextLen: context.length, forwardLen: forward.length, shown: displayedForward, revealed };

  // Bar space picked by the last fit, reused by applyOffset below so a reveal
  // tick can reposition the view without recomputing (and re-compressing) it.
  const barSpaceRef = useRef(2);

  // Whether the pane is phone-width (see COMPACT_FORWARD_BUDGET).
  const isCompactPane = useCallback(() => {
    const container = containerRef.current;
    return !!container && container.clientWidth < COMPACT_CHART_WIDTH;
  }, []);

  // How many Forward candles the fit makes room for: all of them on a wide
  // pane, the shown ones plus a short strip on a compact one.
  const forwardBudget = useCallback(() => {
    const { forwardLen, shown } = lengthsRef.current;
    if (!isCompactPane()) return forwardLen;
    return Math.min(forwardLen, Math.max(shown, 0) + COMPACT_FORWARD_BUDGET);
  }, [isCompactPane]);

  // KLineChart anchors the last visible bar `offsetRightDistance` from the
  // pane's right edge, so under-filling the view (fewer bars than the fitted
  // bar space budgeted for) leaves the empty space on the LEFT, not the
  // right. To keep the reserved-for-forward space where it visually belongs
  // (a strip on the right the reveal fills into) the offset itself carries
  // the not-yet-shown forward's width, shrinking to RIGHT_OFFSET as the
  // reveal completes. This only moves the pane's anchor; it never touches
  // bar space, so it's safe to call on every reveal tick.
  const applyOffset = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const { shown } = lengthsRef.current;
    const remaining = Math.max(0, forwardBudget() - shown);
    chart.setOffsetRightDistance(RIGHT_OFFSET + remaining * barSpaceRef.current);
  }, [forwardBudget]);

  // Fit the bar space to the container's current width. Sized off context
  // plus the full forward (not shownForward) so the reveal appends candles
  // into the reserved right-hand space instead of re-compressing every step.
  const fitToContainer = useCallback(() => {
    const chart = chartRef.current;
    const container = containerRef.current;
    if (!chart || !container) return;
    const { contextLen, revealed } = lengthsRef.current;
    const compactPane = isCompactPane();
    setCompact(compactPane);
    // An anonymized pane's y-axis is collapsed to zero width, so a compact one
    // spends that strip on candles; a wide pane keeps its old, reveal-stable fit.
    const axisReserve = compactPane && !revealed ? 0 : AXIS_RESERVE;
    const barSpace = fitBarSpace(container.clientWidth, contextLen + forwardBudget(), axisReserve);
    barSpaceRef.current = barSpace;
    chart.setBarSpace(barSpace);
    applyOffset();
  }, [applyOffset, forwardBudget, isCompactPane]);

  // Reposition (never resize) the reserved strip as the reveal steps through
  // the Forward. Deliberately separate from fitToContainer: shownForward
  // changing must not re-fit the bar space, only slide the anchor. A compact
  // pane is the exception: its fit follows the shown Forward.
  useEffect(() => {
    if (isCompactPane()) fitToContainer();
    else applyOffset();
  }, [forward.length, displayedForward, applyOffset, fitToContainer, isCompactPane]);

  // A drawing has finished (placed, removed, or cleared): give the pane its
  // scroll back and let the toolbar drop the armed styling. Idempotent, since
  // removing several overlays at once fires it once each.
  const endDrawing = useCallback(() => {
    chartRef.current?.setScrollEnabled(true);
    setDrawingArmed(false);
    onDrawingChangeRef.current?.(null);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      startDrawing: (toolId) => {
        const chart = chartRef.current;
        if (!chart) return;
        // KLineChart reads a press-drag on the pane as a pan, so a player who
        // drags out a trendline instead scrolls the candles away underneath it.
        // Scrolling is off for as long as a tool is armed.
        chart.setScrollEnabled(false);
        setDrawingArmed(true);
        onDrawingChangeRef.current?.(toolId);
        chart.createOverlay({
          name: DRAWING_TOOL_KLINE_NAMES[toolId],
          groupId: DRAWING_GROUP,
          onDrawEnd: () => {
            endDrawing();
            return false;
          },
          onRemoved: () => {
            endDrawing();
            return false;
          },
        });
      },
      clearDrawings: () => {
        chartRef.current?.removeOverlay({ groupId: DRAWING_GROUP });
        endDrawing();
      },
    }),
    [endDrawing],
  );

  // Initialize once; dispose on unmount.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const instance = init(container);
    chartRef.current = instance;
    instance?.setStyles(baseChartStyles());
    fitToContainer();
    setChart(instance);

    // KLineChart v9 doesn't watch its container: without an explicit resize()
    // its canvas keeps the size it was created at, so window resizes and a
    // mobile-width initial layout both leave it stale/misfit. requestAnimationFrame
    // coalesces bursts of ResizeObserver callbacks (e.g. a dragged window edge)
    // into one resize + refit per frame.
    let frame: number | null = null;
    const observer = new ResizeObserver(() => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = null;
        chartRef.current?.resize();
        fitToContainer();
      });
    });
    observer.observe(container);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      observer.disconnect();
      dispose(container);
      chartRef.current = null;
      // Forget indicator panes tied to the disposed chart so a remount recreates
      // them on the new instance (StrictMode double-mount, hot reload).
      indicatorPanes.current.clear();
    };
  }, [fitToContainer]);

  // Gesture policy for the wheel (GRA-130; the whole policy is written up in
  // chartGestures.ts). KLineChart subscribes to `wheel` with
  // `{ passive: false }` and preventDefaults every vertical one before it
  // consults `setZoomEnabled`, so no library switch can give the page its
  // scroll back. This listener is on the container in the CAPTURE phase, i.e.
  // ahead of the library's own listener on the chart element inside it, and
  // simply stops the event from reaching it when the gesture belongs to the
  // page. It prevents nothing itself, so the browser scrolls normally, and it
  // touches no pointer event, so the crosshair and the drawing tools are
  // unaffected.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onWheel = (e: WheelEvent) => {
      if (wheelIntent(e) === "page") e.stopPropagation();
    };
    container.addEventListener("wheel", onWheel, { capture: true });
    return () => container.removeEventListener("wheel", onWheel, { capture: true });
  }, []);

  // Whether this round has any volume worth a pane (GRA-133). Checked across
  // the whole round, Context and Forward, so the pane does not pop in mid-
  // reveal just because the answer candles happen to carry volume the
  // Context did not. Most rounds have no volume field at all today, so this
  // is false for them and the VOL toggle is a no-op until real volume data
  // exists.
  const roundHasVolume = useMemo(
    () => hasMeaningfulVolume([...context, ...forward]),
    [context, forward],
  );

  // Reconcile indicators: create the newly-added, remove the newly-removed.
  // "vol" is filtered out of `want` when the round has no meaningful volume,
  // so a chart never grows a pane full of zero-height bars (GRA-133); this
  // re-runs on roundHasVolume too, so a pane already open closes the moment
  // a no-volume round loads, and vice versa.
  useEffect(() => {
    if (!chart) return;
    const want = (indicators ?? []).filter((id) => id !== "vol" || roundHasVolume);
    for (const id of want) {
      if (!indicatorPanes.current.has(id)) {
        const klineName = INDICATOR_KLINE_NAMES[id];
        // Object form (`{ name, styles, ... }`), not the bare kline name
        // string, so this instance carries the app's own theme colors
        // instead of KLineChart's default purple/orange/blue/magenta lines
        // and teal/crimson volume bars (see `indicatorStyles`). RSI also
        // gets a custom `draw` for its 30/70 reference lines, drawn under
        // the library's own line figures (it returns `false`, which tells
        // KLineChart to still run its normal figure-driven draw afterward —
        // see `IndicatorView.drawImp`'s `isCover` check).
        const indicatorCreate = {
          name: klineName,
          styles: indicatorStyles(id) as unknown as Partial<IndicatorStyle>,
          ...(id === "rsi" ? { draw: drawRsiReferenceLines } : {}),
        };
        const paneId = MAIN_PANE_INDICATORS.has(id)
          ? // `true`, not `false`: KLineChart 9.8.12's `IndicatorStore.addInstance`
            // replaces the *whole* pane's indicator array when `isStack` is
            // false, not just the same-named instance, which would silently
            // wipe out the candle pane's other app indicators (`appRange`,
            // `appVerticalMarkers` — see their own `createIndicator` calls).
            chart.createIndicator(indicatorCreate, true, { id: CANDLE_PANE_ID })
          : chart.createIndicator(indicatorCreate);
        if (paneId) indicatorPanes.current.set(id, paneId);
      }
    }
    for (const [id, paneId] of [...indicatorPanes.current]) {
      if (!want.includes(id)) {
        chart.removeIndicator(paneId, INDICATOR_KLINE_NAMES[id]);
        indicatorPanes.current.delete(id);
      }
    }
  }, [indicators, chart, roundHasVolume]);

  // Drive the reveal animation: step the Forward in, or reset when hidden.
  useEffect(() => {
    if (!revealed || forward.length === 0) {
      setShownForward(0);
      return;
    }
    // The animation's actual destination (GRA-147): the round's resolution,
    // not necessarily the whole Forward. Clamped defensively so a stale or
    // out-of-range revealTo (a fresh Round's resolution not yet computed
    // against this Forward) can't leave the animation stuck short of, or
    // reaching past, what's actually there.
    const target = Math.max(0, Math.min(revealTo ?? forward.length, forward.length));
    // Derive the per-candle step from a target total duration, so the reveal
    // stays fast whether it's stepping through 1 candle or 40 (spec §9).
    const stepMs = revealStepMs ?? forwardStepMs(target, revealDurationMs);
    if (stepMs <= 0 || target === 0) {
      setShownForward(target);
      onRevealCompleteRef.current?.();
      return;
    }
    setShownForward(0);
    let shown = 0;
    const id = setInterval(() => {
      shown += 1;
      setShownForward(shown);
      if (shown >= target) {
        clearInterval(id);
        onRevealCompleteRef.current?.();
      }
    }, stepMs);
    return () => clearInterval(id);
    // Depend on forward.length, not the array identity: a parent re-rendering
    // with a fresh (equal) forward array must not restart the animation.
  }, [revealed, forward.length, revealStepMs, revealDurationMs, revealTo]);

  // Re-fit only when a new round's Context arrives, not on each reveal step:
  // shownForward changing must not re-fit and re-compress mid-animation.
  // The reveal also re-fits, for a compact pane whose axis opens at that moment
  // (see fitToContainer). On a wide pane the inputs are unchanged, so this
  // re-applies the same bar space.
  useEffect(() => {
    fitToContainer();
  }, [context, revealed, fitToContainer]);

  const overlayList = overlays ?? NO_OVERLAYS;
  const overlayListRef = useRef(overlayList);
  overlayListRef.current = overlayList;

  // The app overlays are torn down and rebuilt whenever this key changes, so a
  // draggable line's own price is deliberately left out of it: while the
  // pointer holds a line, `overlays` arrives as a new array on every mouse
  // move, and recreating the group mid-drag would destroy the very overlay the
  // pointer is holding. A draggable line's price is pushed into the live
  // overlay instead (see the effect after this one).
  const overlayKey = useMemo(
    () =>
      JSON.stringify(
        overlayList.map((o) => ("value" in o && o.draggable ? { ...o, value: null } : o)),
      ),
    [overlayList],
  );

  // Just the draggable lines' prices, so the push-in-place effect below runs
  // when one of them moves rather than on every render of the parent.
  const dragValueKey = useMemo(
    () =>
      JSON.stringify(
        overlayList.filter((o) => "value" in o && o.draggable).map((o) => ("value" in o ? o.value : null)),
      ),
    [overlayList],
  );

  // True while the pointer is holding a draggable line. KLineChart moves the
  // point itself during a drag, so the effect below must not push the caller's
  // value back into it: that value is one mouse-move behind (see
  // `dragHandlers`), and writing it back would drag the line backwards.
  const draggingRef = useRef(false);

  // KLineChart keeps a crosshair on the pane and has no hover cursor of its
  // own for an unlocked overlay, so the "you can grab this" affordance is set
  // here, on enter and leave of a draggable line. The crosshair it had is put
  // back on leave rather than simply cleared: the library sets it inline, and
  // clearing would leave the pane with no cursor at all.
  const cursorBeforeHoverRef = useRef("");
  const setPaneCursor = useCallback((cursor: string | null) => {
    const dom = chartRef.current?.getDom(CANDLE_PANE_ID, DomPosition.Main);
    if (!dom) return;
    if (cursor === null) {
      dom.style.cursor = cursorBeforeHoverRef.current;
      return;
    }
    cursorBeforeHoverRef.current = dom.style.cursor;
    dom.style.cursor = cursor;
  }, []);

  /**
   * The KLineChart wiring that makes one app overlay grabbable.
   *
   * `onPressedMoving` fires *before* the library moves the point, so the price
   * read off it is where the line sat on the previous mouse move: a frame
   * behind the pointer, which is invisible at mouse-move rate, and
   * `onPressedMoveEnd` fires once with the settled point so the caller never
   * keeps that last frame's value. Both return false, leaving the library to
   * perform the move itself rather than this reimplementing pixel-to-price.
   */
  const dragHandlers = useCallback(
    (line: OverlayLine) => {
      const report = (event: OverlayEvent) => {
        const value = event.overlay?.points?.[0]?.value;
        if (typeof value === "number") onOverlayDragRef.current?.(line, value);
      };
      return {
        onMouseEnter: () => {
          setPaneCursor("ns-resize");
          return false;
        },
        onMouseLeave: () => {
          setPaneCursor(null);
          return false;
        },
        onPressedMoving: (event: OverlayEvent) => {
          draggingRef.current = true;
          report(event);
          return false;
        },
        onPressedMoveEnd: (event: OverlayEvent) => {
          draggingRef.current = false;
          report(event);
          return false;
        },
      };
    },
    [setPaneCursor],
  );

  // Re-apply data / styles / overlays whenever the visible candles or inputs change.
  useEffect(() => {
    if (!chart) return;
    const candles = withForward(context, forward, displayedForward);
    // Decimals before the data, so the first paint already carries them.
    const precision = pricePrecision(candles);
    chart.setPriceVolumePrecision(precision, 0);
    chart.setStyles({ candle: { tooltip: { custom: tooltipLegends(precision) } } });
    chart.applyNewData(toKLineData(candles));
    const captioned = revealed && !!caption;
    chart.setStyles(anonymizationStyles(revealed, compact, captioned));
    setAxisWidth(chart.getSize(CANDLE_PANE_ID, DomPosition.YAxis)?.width ?? 0);

    // Redraw only the app overlays (barriers, reveal markers) each update;
    // user drawings live in a separate group and survive data updates and
    // the reveal.
    chart.removeOverlay({ groupId: APP_GROUP });
    const currentOverlays = overlayListRef.current;
    const start = startLine(context, currentOverlays);
    const resolution = resolutionMarker(context, forward, revealed, revealTo);
    // Shared by the Last close line and the Decision marker below: both are a
    // reference point (where price, or the read, was struck from) rather
    // than a level the player set, so both draw dashed instead of solid.
    const DASH = { style: LineType.Dashed, dashedValue: [4, 4] };
    // Shorter dashes, so a Resolved marker reads as dotted rather than a
    // second dashed Decision line (see `resolutionMarker`).
    const DOT = { style: LineType.Dashed, dashedValue: [2, 4] };
    const lines = [
      ...currentOverlays,
      ...(start ? [start] : []),
      ...(resolution ? [resolution] : []),
    ];
    for (const [index, overlay] of lines.entries()) {
      const color = cachedToneColor(overlay.tone);
      // A two-point annotation (the practice packs' drawn lines). Skipped
      // whole while either end sits past what is currently applied, the same
      // way a vertical marker is: during the reveal's step-in animation an
      // `after` annotation can reference a Forward candle that is not on the
      // chart yet, and it simply appears once both its ends are.
      if ("segment" in overlay) {
        const [from, to] = overlay.segment;
        if (from.dataIndex < 0 || from.dataIndex >= candles.length) continue;
        if (to.dataIndex < 0 || to.dataIndex >= candles.length) continue;
        chart.createOverlay({
          name: "labeledSegment",
          points: [
            { dataIndex: from.dataIndex, value: from.value },
            { dataIndex: to.dataIndex, value: to.value },
          ],
          groupId: APP_GROUP,
          lock: true,
          ...(overlay.label ? { extendData: overlay.label } : {}),
          // The same chip as every other app label (bordered, on the page's
          // own background) rather than raw canvas text, so an annotation
          // reads over the candles it is drawn across.
          ...(color
            ? {
                styles: {
                  line: { color },
                  text: labelStyle(color, cachedThemeColor("bg"), cachedThemeFont()),
                },
              }
            : {}),
        });
        continue;
      }
      if ("value" in overlay) {
        // A phone-width pane keeps its chips short: the Last close line goes
        // unlabeled (its dashes and the axis tag say enough), and a line with a
        // `compactLabel` wears that instead.
        const label = !compact
          ? overlay.label
          : overlay === start
            ? undefined
            : (overlay.compactLabel ?? overlay.label);
        // Dashed for the Last close line, and for any line the caller asked for it
        // on (the Trade Loop's Entry): the barriers are levels the player set,
        // these are where price was when they set them.
        const dashed = overlay === start || overlay.dashed === true;
        const tint = color
          ? {
              styles: {
                line: { color, ...(dashed ? DASH : {}) },
                text: labelStyle(color, cachedThemeColor("bg"), cachedThemeFont()),
              },
            }
          : dashed
            ? { styles: { line: DASH } }
            : {};
        const draggable = overlay.draggable === true;
        chart.createOverlay({
          name: label ? "labeledLine" : "horizontalStraightLine",
          points: [{ value: overlay.value }],
          groupId: APP_GROUP,
          // App lines are readouts, not drawings, so the player must not drag
          // them — unless the caller marked this one as a level it wants moved
          // by hand (the Trade Loop's Stop and Target, while still a draft).
          lock: !draggable,
          ...(label ? { extendData: label } : {}),
          ...(draggable
            ? { id: draggableId(overlay.label, index), ...dragHandlers(overlay) }
            : {}),
          // The label's chip is tinted the same as its line (text and border,
          // see `labelStyle`) so an upper/lower pair reads apart at a glance.
          ...tint,
        });
        continue;
      }
      // A vertical marker only makes sense once its candle is actually
      // plotted: during the reveal's step-in animation the resolution
      // candle's dataIndex can point past what's currently applied, and
      // KLineChart has nothing to anchor to there. Skipping it here (rather
      // than the caller tracking the animation) means it simply appears the
      // moment its candle lands, in step with the reveal.
      if (overlay.dataIndex < 0 || overlay.dataIndex >= candles.length) continue;
      // Only the label chip is an overlay now; the line itself is drawn by
      // the `appVerticalMarkers` indicator below, under the candles (see
      // `VERTICAL_MARKER_INDICATOR_NAME`).
      const tint = color
        ? { styles: { text: labelStyle(color, cachedThemeColor("bg"), cachedThemeFont()) } }
        : {};
      chart.createOverlay({
        name: "labeledVerticalLine",
        points: [{ dataIndex: overlay.dataIndex }],
        groupId: APP_GROUP,
        lock: true,
        extendData: {
          // No chip on a phone-width pane: the dashed Decision and dotted
          // Resolved lines read apart on their own, and the chips crowd it.
          text: compact ? "" : overlay.label,
          anchor: overlay.anchor,
          clearance: captioned ? CAPTIONED_MARKER_CLEARANCE : TOP_MARKER_CLEARANCE,
        },
        ...tint,
      });
    }

    // The vertical markers' lines (see `VERTICAL_MARKER_INDICATOR_NAME`):
    // rebuilt from the same `lines` this effect already computed, so they
    // stay in step with whatever overlays were just (re)drawn above.
    chart.removeIndicator(CANDLE_PANE_ID, VERTICAL_MARKER_INDICATOR_NAME);
    const verticalMarkers = lines
      .filter((overlay): overlay is Extract<OverlayLine, { dataIndex: number }> => "dataIndex" in overlay)
      .filter((overlay) => overlay.dataIndex >= 0 && overlay.dataIndex < candles.length)
      .map((overlay) => ({
        dataIndex: overlay.dataIndex,
        color: cachedToneColor(overlay.tone) ?? cachedThemeColor("muted") ?? "#787878",
        dash: overlay.dotted ? DOT.dashedValue : DASH.dashedValue,
      }));
    if (verticalMarkers.length > 0) {
      registerVerticalMarkerIndicator(verticalMarkers);
      // `isStack: true` (see the comment on `MAIN_PANE_INDICATORS`'s own
      // `createIndicator` call above and `RANGE_INDICATOR_NAME`'s below):
      // KLineChart 9.8.12's `IndicatorStore.addInstance` replaces the whole
      // pane's indicator array when `isStack` is false, rather than only the
      // same-named instance — so a `false` here silently wiped out whatever
      // `appRange`/a user-toggled MA or BOLL indicator the candle pane
      // already carried (and, created in the other order, would itself get
      // wiped by their own `false`-stacked calls). `true` appends instead,
      // which is safe here since it always follows a `removeIndicator` by
      // this exact name a few lines up, so there is never a real duplicate.
      chart.createIndicator(VERTICAL_MARKER_INDICATOR_NAME, true, { id: CANDLE_PANE_ID });
    }

    // Widen the pane's y-axis to every price line just drawn (see
    // `registerRangeIndicator`): KLineChart scales the candle pane to the
    // visible candles only, so a barrier or Entry/Stop/Target beyond them
    // would otherwise sit off-canvas with just its label chip clamped to the
    // pane edge. Keyed on the same `lines` this effect already built (and so,
    // like the rest of it, on `overlayKey` rather than the live `overlays`
    // array): a draggable line's own price must not retrigger this mid-drag,
    // which recreating an indicator on every mouse move would make visible
    // as a stutter. `chart.removeIndicator` first either way, so a Round
    // that no longer needs widening (every line back within the candles'
    // own range) drops back to the candles alone instead of keeping a stale
    // one.
    chart.removeIndicator(CANDLE_PANE_ID, RANGE_INDICATOR_NAME);
    const priceValues = [
      ...lines
        .filter((overlay): overlay is Extract<OverlayLine, { value: number }> => "value" in overlay)
        .map((overlay) => overlay.value),
      // A drawn annotation's own endpoints count too: one struck above every
      // candle would otherwise be clipped to the pane edge exactly as an
      // out-of-range barrier was.
      ...lines
        .filter(
          (overlay): overlay is Extract<OverlayLine, { segment: unknown }> =>
            "segment" in overlay,
        )
        .flatMap((overlay) => overlay.segment.map((point) => point.value)),
    ];
    if (priceValues.length > 0) {
      registerRangeIndicator(Math.min(...priceValues), Math.max(...priceValues));
      // `true`: see the comment on `appVerticalMarkers`'s own `createIndicator`
      // call above. `false` here would wipe that indicator's line right back
      // out (or, run in the other order, this one) since both share the
      // candle pane.
      chart.createIndicator(RANGE_INDICATOR_NAME, true, { id: CANDLE_PANE_ID });
    }
    // `overlayKey`, not the `overlays` array: see the comment on it for why a
    // draggable line's own price must not land here.
  }, [
    context,
    forward,
    revealed,
    revealTo,
    overlayKey,
    displayedForward,
    compact,
    chart,
    caption,
    dragHandlers,
    cachedToneColor,
    cachedThemeColor,
    cachedThemeFont,
  ]);

  // Move a draggable line to the price the caller now holds, without touching
  // the rest of the app group. This is what keeps the line following the field
  // when the player types or steps a level, and what snaps it onto the rounded
  // value once a drag ends.
  useEffect(() => {
    if (!chart || draggingRef.current) return;
    for (const [index, overlay] of overlayListRef.current.entries()) {
      if (!("value" in overlay) || !overlay.draggable) continue;
      chart.overrideOverlay({
        id: draggableId(overlay.label, index),
        points: [{ value: overlay.value }],
      });
    }
  }, [chart, dragValueKey]);

  const visible = withForward(context, forward, displayedForward);
  const pct = pctFromStart(visible);

  return (
    // Sized by flex, not by `h-full`: a percentage height needs every ancestor
    // to have a definite one, which stops being true below `sm` where the shell
    // is `min-h-screen` and the page scrolls. As a stretched flex item the
    // canvas fills its box either way.
    <div className={className ?? "relative flex min-h-0 w-full flex-1 flex-col"}>
      <div
        ref={containerRef}
        data-testid="chart-canvas"
        className="min-h-0 w-full flex-1"
        // Touch half of the gesture policy (chartGestures.ts): the browser
        // keeps vertical panning, so a swipe over the chart scrolls the page,
        // while horizontal drags and pinches still reach the chart. An armed
        // drawing tool takes the whole gesture back for the stroke.
        style={{ touchAction: chartTouchAction(drawingArmed) }}
      />
      {/* Labeled "from chart start" (GRA-124): this measures from the first
          plotted candle, which is not always the decision point (a wider
          Context-length setting plots candles before it) and can disagree in
          sign with a resolution's "from decision" percent even on the same
          Round: two real measurements, not a contradiction, but only if each
          says what it's measuring. */}
      {revealed && caption && (
        <div
          // `left-3`: flush with the OHLC readout's text under it, which
          // KLineChart starts 4px in plus an 8px text margin. `px-1 -ml-1`
          // gives the opaque backing box some breathing room around the text
          // (so the chart's grid lines don't visibly cross through it)
          // without shifting the text itself off that `left-3` flush edge.
          //
          // A compact pane has no room for the caption and the "% from start"
          // readout on one line, so the caption steps down a size and puts a
          // trailing "(date range)" on its own muted line underneath.
          className={`pointer-events-none absolute left-3 top-1 z-10 -ml-1 rounded-sm bg-bg px-1 py-0.5 font-mono text-fg ${
            compact ? "text-xs" : "text-sm"
          }`}
          aria-label="instrument"
        >
          {compact ? <CompactCaption caption={caption} /> : caption}
        </div>
      )}
      <div
        // `right: axisWidth + 4` with `px-1`: the box's own right padding (4px)
        // sits between the text and the box edge, so the box is nudged 4px
        // closer to the axis to keep the number flush where it always was.
        className="pointer-events-none absolute top-1 z-10 rounded-sm bg-bg px-1 py-0.5 text-right font-mono text-sm"
        style={{ right: axisWidth + 4 }}
        aria-label="percent from start"
      >
        <span className={pct > 0 ? "text-up" : pct < 0 ? "text-down" : "text-muted"}>
          {pct >= 0 ? "+" : ""}
          {pct.toFixed(2)}%
        </span>
        {!hidePercentReference && !compact && (
          <span className="block text-[10px] uppercase tracking-wide text-muted">
            from chart start
          </span>
        )}
      </div>
    </div>
  );
});
